<script lang="ts">
    import { onMount } from 'svelte';
    import { api } from '../lib/api';
    import { envs } from '../lib/stores/environment.svelte';
    import { toast } from '../lib/stores/toast.svelte';
    import type { Environment } from '../lib/stores/environment.svelte';
    import type { Consumer } from '../lib/types';

    let creating = $state(false);
    let editing = $state<Environment | null>(null);
    let form = $state({ name: '', description: '', themeColor: '#3b82f6', allowedIps: '' });

    // t_de11df77 (amendment #3): show WHICH environment this vault instance
    // serves. Exactly one consumer row fleet-wide carries is_self; its
    // environment gets the badge. Null while loading / none set (degraded).
    let selfEnvId = $state<string | null>(null);

    onMount(() => {
        envs.load();
        refreshSelfEnv();
    });

    async function refreshSelfEnv() {
        try {
            const rows = await api<Consumer[]>('/api/consumers');
            selfEnvId = rows.find((c) => c.isSelf)?.environmentId ?? null;
        } catch {
            selfEnvId = null; // list view must never break on this lookup
        }
    }

    function startEdit(e: Environment) {
        editing = e;
        form = { name: e.name, description: e.description ?? '', themeColor: e.themeColor, allowedIps: e.allowedIps.join(', ') };
    }

    function startNew() {
        creating = true;
        editing = null;
        form = { name: '', description: '', themeColor: '#3b82f6', allowedIps: '' };
    }

    async function save() {
        const body = {
            name: form.name,
            description: form.description || null,
            themeColor: form.themeColor,
            allowedIps: form.allowedIps.split(',').map((s) => s.trim()).filter(Boolean),
        };
        try {
            if (editing) await api(`/api/environments/${editing.id}`, { method: 'PATCH', body: JSON.stringify(body) });
            else         await api('/api/environments', { method: 'POST', body: JSON.stringify(body) });
            toast.success('Saved');
            creating = false; editing = null;
            envs.load();
        } catch (err: any) { toast.error(err.message); }
    }

    async function del(e: Environment) {
        if (!confirm(`Delete environment "${e.name}"? All its secrets will be deleted too.`)) return;
        try {
            await api(`/api/environments/${e.id}`, { method: 'DELETE' });
            toast.success('Deleted');
            envs.load();
            refreshSelfEnv();
        } catch (err: any) {
            // t_de11df77: a 409 here means the environment holds the is_self
            // anchor — the message tells the operator to clear self-identity.
            toast.error(err.message);
            refreshSelfEnv();
        }
    }
</script>

<div class="page-header">
    <h1>Environments</h1>
    <button class="primary" onclick={startNew}>+ New Environment</button>
</div>

<div class="grid">
    {#each envs.list as e}
        <div class="env-card" style="border-left: 4px solid {e.themeColor};">
            <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                <h3>
                    {e.name}
                    {#if e.id === selfEnvId}<span class="badge self-env-badge" title="Boot hydration resolves THIS vault instance's secrets (JWT_SECRET / SERVICE_SECRET / MOTHER_API_URL) from this environment's Global scope.">This vault's own environment</span>{/if}
                </h3>
                <button
                        class="ghost small mono"
                        onclick={() => { navigator.clipboard.writeText(e.id); toast.success('Environment ID copied!'); }}
                        title="Copy Environment ID"
                        style="padding: 2px 6px; font-size: 11px;"
                >
                    📋 {e.id.slice(0, 8)}...
                </button>
            </div>

            {#if e.description}<p class="muted">{e.description}</p>{/if}
            <div class="ips">
                <strong>Allowed IPs:</strong>
                {#if e.allowedIps.length === 0}
                    <span class="badge warning">🌐 Wildcard (no filtering)</span>
                {:else}
                    {#each e.allowedIps as ip}<code class="mono">{ip}</code>{/each}
                {/if}
            </div>
            <div class="actions">
                <button onclick={() => startEdit(e)}>Edit</button>
                <button class="ghost" onclick={() => del(e)}>🗑</button>
            </div>
        </div>
    {:else}
        <p class="muted">No environments yet.</p>
    {/each}
</div>

{#if creating || editing}
    <dialog open>
        <header><h2>{editing ? 'Edit Environment' : 'New Environment'}</h2></header>
        <div class="body">
            <label>Name<input bind:value={form.name} /></label>
            <label>Description<input bind:value={form.description} /></label>
            <label>Theme Color
                <input type="color" bind:value={form.themeColor} style="width:60px;height:32px;padding:2px;" />
            </label>
            <label>
                Allowed IPs <span class="muted">(comma-separated CIDR or exact IPs, empty = wildcard)</span>
                <textarea bind:value={form.allowedIps} rows="2" placeholder="10.0.0.0/24, 1.2.3.4" class="mono"></textarea>
            </label>
        </div>
        <footer>
            <button onclick={() => { creating = false; editing = null; }}>Cancel</button>
            <button class="primary" onclick={save} disabled={!form.name}>Save</button>
        </footer>
    </dialog>
{/if}

<style>
    .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }
    .env-card { background: var(--bg-1); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 16px; }
    .env-card h3 { margin: 0 0 6px; }
    .ips { margin: 12px 0; font-size: 12px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
    .ips code { background: var(--bg-0); padding: 2px 6px; border-radius: 4px; }
    .actions { display: flex; gap: 8px; margin-top: 12px; }
    .self-env-badge { display: inline-block; margin-left: 8px; padding: 2px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; vertical-align: middle; background: rgba(59,130,246,0.15); border: 1px solid rgba(59,130,246,0.5); color: var(--text-0); }
    header { padding: 16px 20px; border-bottom: 1px solid var(--border); }
    header h2 { margin: 0; font-size: 16px; }
    .body { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
    footer { padding: 12px 20px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 8px; }
    label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
</style>